import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'

// Helper para obtener nombre del cliente (filtra "N/A")
const getClientName = (client) => {
  // Si viene del serializer como cliente_name
  if (typeof client === 'string') {
    return client === 'N/A' ? '' : client
  }
  // Si es un objeto cliente
  if (client?.razon_social) return client.razon_social
  if (client?.business_name) return client.business_name
  const parts = []
  if (client?.nombre) parts.push(client.nombre)
  if (client?.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return parts.join(' ')
  return client?.name || ''
}

const BillingConfigurations = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  
  // Filtros
  const { clientId: filterCliente, setClientId: setFilterCliente, selectDisabled } = useSyncedLocalClientId()
  const [filterActivo, setFilterActivo] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Formulario
  const [formData, setFormData] = useState({
    cliente: '',
    serie: '',
    tipos_permitidos: [],
    folio_inicial: 1,
    monto_maximo: '',
    activo: true,
  })

  const [errors, setErrors] = useState({})

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-billing-config',
    async () => {
      const response = await api.get('/clients/clients/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener configuraciones de facturación
  const { data: configsData, isLoading } = useQuery(
    ['billing-configs', filterCliente, filterActivo, searchTerm],
    async () => {
      const params = {}
      if (filterCliente) params.cliente_id = filterCliente
      if (filterActivo !== '') params.activo = filterActivo === 'true'
      if (searchTerm) params.search = searchTerm
      
      const response = await api.get('/financial/configuraciones-facturacion/', { params })
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const configs = configsData || []

  // Mutación para crear/actualizar
  const saveMutation = useMutation(
    async (data) => {
      const payload = {
        ...data,
        tipos_permitidos: data.tipos_permitidos || [],
        folio_inicial: parseInt(data.folio_inicial) || 1,
        monto_maximo: data.monto_maximo ? parseFloat(data.monto_maximo) : null,
      }
      
      if (editingConfig) {
        return api.put(`/financial/configuraciones-facturacion/${editingConfig.id}/`, payload)
      } else {
        return api.post('/financial/configuraciones-facturacion/', payload)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('billing-configs')
        queryClient.invalidateQueries('facturacion-config')
        queryClient.invalidateQueries('facturacion-config-complemento')
        toast.success(editingConfig ? 'Configuración actualizada correctamente' : 'Configuración creada correctamente')
        handleCloseModal()
      },
      onError: (error) => {
        const errorData = error.response?.data
        if (errorData) {
          if (typeof errorData === 'object') {
            setErrors(errorData)
            toast.error('Error al guardar: ' + Object.values(errorData).flat().join(', '))
          } else {
            toast.error('Error: ' + errorData)
          }
        } else {
          toast.error('Error al guardar la configuración')
        }
      },
    }
  )

  // Mutación para eliminar
  const deleteMutation = useMutation(
    async (id) => {
      return api.delete(`/financial/configuraciones-facturacion/${id}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('billing-configs')
        queryClient.invalidateQueries('facturacion-config')
        queryClient.invalidateQueries('facturacion-config-complemento')
        toast.success('Configuración eliminada correctamente')
      },
      onError: () => {
        toast.error('Error al eliminar la configuración')
      },
    }
  )

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config)
      const siguienteFolio = (parseInt(config.folio_actual, 10) || 0) + 1
      setFormData({
        cliente: config.cliente || config.cliente_id || '',
        serie: config.serie || '',
        tipos_permitidos: config.tipos_permitidos || [],
        folio_inicial: siguienteFolio,
        monto_maximo: config.monto_maximo || '',
        activo: config.activo !== undefined ? config.activo : true,
      })
    } else {
      setEditingConfig(null)
      setFormData({
        cliente: '',
        serie: '',
        tipos_permitidos: [],
        folio_inicial: 1,
        monto_maximo: '',
        activo: true,
      })
    }
    setErrors({})
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingConfig(null)
    setFormData({
      cliente: '',
      serie: '',
      tipos_permitidos: [],
      folio_inicial: 1,
      monto_maximo: '',
      activo: true,
    })
    setErrors({})
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors({})
    saveMutation.mutate(formData)
  }

  const handleDelete = (config) => {
    const clienteName = config.cliente_name || 
                       (config.cliente ? getClientName(config.cliente) : '') ||
                       (typeof config.cliente === 'object' ? getClientName(config.cliente) : '') ||
                       'Cliente'
    if (window.confirm(`¿Estás seguro de eliminar la configuración de facturación para ${clienteName} - Serie: ${config.serie || 'Sin serie'}?`)) {
      deleteMutation.mutate(config.id)
    }
  }

  const handleTipoChange = (tipo, checked) => {
    setFormData(prev => {
      const tipos = prev.tipos_permitidos || []
      if (checked) {
        return { ...prev, tipos_permitidos: [...tipos, tipo] }
      } else {
        return { ...prev, tipos_permitidos: tipos.filter(t => t !== tipo) }
      }
    })
  }

  // Filtrar configuraciones
  const filteredConfigs = configs.filter(config => {
    // El API devuelve el contribuyente en `cliente`, no en `cliente_id`. Comparar contra
    // `cliente_id` daba `undefined !== "51"` --siempre verdadero-- y la tabla salia **vacia**
    // en cuanto se elegia un cliente del select. El backend ya filtra por `cliente_id` en el
    // query param, asi que esto es la segunda red, no la unica.
    if (filterCliente && String(config.cliente ?? '') !== String(filterCliente)) return false
    if (filterActivo !== '' && config.activo?.toString() !== filterActivo) return false
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      const clienteName = (config.cliente_name || 
                          (config.cliente ? getClientName(config.cliente) : '') ||
                          (typeof config.cliente === 'object' ? getClientName(config.cliente) : '') ||
                          '').toLowerCase()
      const serie = (config.serie || '').toLowerCase()
      if (!clienteName.includes(searchLower) && !serie.includes(searchLower)) return false
    }
    return true
  })

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Configuraciones de Facturación</h5>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-circle me-1"></i>
            Nueva Configuración
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Filtros */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={filterCliente}
                  onChange={(e) => setFilterCliente(e.target.value)}
                  disabled={selectDisabled}
                >
                  <option value="">Todos los clientes</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {getClientName(client)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterActivo}
                  onChange={(e) => setFilterActivo(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="true">Activos</option>
                  <option value="false">Inactivos</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar por cliente o serie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : filteredConfigs.length === 0 ? (
            <Alert variant="info">No se encontraron configuraciones de facturación</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th style={{ minWidth: '200px' }}>Cliente</th>
                  <th style={{ minWidth: '100px' }}>Serie</th>
                  <th style={{ minWidth: '150px' }}>Tipos Permitidos</th>
                  <th style={{ minWidth: '100px' }}>Folio Inicial</th>
                  <th style={{ minWidth: '100px' }}>Folio Actual</th>
                  <th style={{ minWidth: '120px' }}>Monto Máximo</th>
                  <th style={{ minWidth: '80px' }}>Estado</th>
                  <th style={{ minWidth: '180px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredConfigs.map(config => {
                  // Obtener nombre del cliente del serializer o del objeto cliente
                  const clienteName = config.cliente_name || 
                                     (config.cliente ? getClientName(config.cliente) : '') ||
                                     (typeof config.cliente === 'object' ? getClientName(config.cliente) : '') ||
                                     'N/A'
                  
                  return (
                    <tr key={config.id}>
                      <td>{clienteName}</td>
                      <td><strong>{config.serie || 'Sin serie'}</strong></td>
                      <td>
                        {config.tipos_permitidos && config.tipos_permitidos.length > 0 ? (
                          <div>
                            {config.tipos_permitidos.map(tipo => {
                              const tiposMap = { normal: 'Normal', global: 'Global', complemento: 'Complemento' }
                              return (
                                <Badge key={tipo} bg="secondary" className="me-1">
                                  {tiposMap[tipo] || tipo}
                                </Badge>
                              )
                            })}
                          </div>
                        ) : (
                          <span className="text-muted">Sin tipos</span>
                        )}
                      </td>
                      <td>{config.folio_inicial || 1}</td>
                      <td><strong>{String(config.folio_actual || 0).padStart(6, '0')}</strong></td>
                      <td>
                        {config.monto_maximo ? (
                          `${formatCurrency(config.monto_maximo)}`
                        ) : (
                          <span className="text-muted">Sin límite</span>
                        )}
                      </td>
                      <td>
                        {config.activo ? (
                          <Badge bg="success">Activo</Badge>
                        ) : (
                          <Badge bg="secondary">Inactivo</Badge>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', minWidth: '200px', textAlign: 'center' }}>
                        <div className="d-flex gap-2 justify-content-center align-items-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenModal(config)}
                            title="Editar configuración"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            <span>Editar</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config)}
                            title="Eliminar configuración"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear/editar */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingConfig ? 'Editar Configuración de Facturación' : 'Nueva Configuración de Facturación'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    value={formData.cliente}
                    onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                    isInvalid={!!errors.cliente}
                    required
                    disabled={!!editingConfig}
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients?.map(client => (
                      <option key={client.id} value={client.id}>
                        {getClientName(client)}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.cliente && <Form.Control.Feedback type="invalid">{errors.cliente}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Serie</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: FAC, ING, EGR"
                    value={formData.serie}
                    onChange={(e) => setFormData({ ...formData, serie: e.target.value.toUpperCase() })}
                    isInvalid={!!errors.serie}
                    maxLength={10}
                  />
                  <Form.Text className="text-muted">
                    Máximo 10 caracteres. Déjalo vacío para &quot;Sin serie&quot;
                  </Form.Text>
                  {errors.serie && <Form.Control.Feedback type="invalid">{errors.serie}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Tipos de Factura Permitidos *</Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  id="tipo_normal"
                  label="Factura Normal"
                  checked={formData.tipos_permitidos.includes('normal')}
                  onChange={(e) => handleTipoChange('normal', e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="tipo_global"
                  label="Factura Global"
                  checked={formData.tipos_permitidos.includes('global')}
                  onChange={(e) => handleTipoChange('global', e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="tipo_complemento"
                  label="Complemento de Pago"
                  checked={formData.tipos_permitidos.includes('complemento')}
                  onChange={(e) => handleTipoChange('complemento', e.target.checked)}
                />
              </div>
              {errors.tipos_permitidos && (
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                  {Array.isArray(errors.tipos_permitidos) ? errors.tipos_permitidos.join(', ') : errors.tipos_permitidos}
                </Form.Control.Feedback>
              )}
              {formData.tipos_permitidos.length === 0 && (
                <Form.Text className="text-danger">Se debe seleccionar al menos un tipo</Form.Text>
              )}
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Folio Inicial *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.folio_inicial}
                    onChange={(e) => setFormData({ ...formData, folio_inicial: parseInt(e.target.value) || 1 })}
                    isInvalid={!!errors.folio_inicial}
                    required
                  />
                  <Form.Text className="text-muted">
                    {editingConfig
                      ? 'Este valor representa el siguiente folio a timbrar para la serie. Debe ser mayor al último folio usado.'
                      : 'Número inicial de folio para comenzar a facturar.'}
                  </Form.Text>
                  {errors.folio_inicial && <Form.Control.Feedback type="invalid">{errors.folio_inicial}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Monto Máximo</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Sin límite"
                    value={formData.monto_maximo}
                    onChange={(e) => setFormData({ ...formData, monto_maximo: e.target.value })}
                    isInvalid={!!errors.monto_maximo}
                  />
                  <Form.Text className="text-muted">
                    Monto máximo permitido por factura (opcional)
                  </Form.Text>
                  {errors.monto_maximo && <Form.Control.Feedback type="invalid">{errors.monto_maximo}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="activo"
                    label="Activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saveMutation.isLoading || formData.tipos_permitidos.length === 0}
            >
              {saveMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                editingConfig ? 'Actualizar' : 'Crear'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default BillingConfigurations
